/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Platform Multi-App Template Switcher & Catalog View (Task 2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutTemplate,
  Globe,
  Laptop,
  Shield,
  CheckCircle2,
  ExternalLink,
  AlertTriangle,
  RotateCw,
  Plus,
  X,
  Sparkles,
  Info,
  Check,
  Loader2,
  CheckCheck,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import {
  type PlatformAppTarget,
  type TemplateMetadata,
  type PlatformTemplatesSetting,
  DEFAULT_PLATFORM_TEMPLATES,
} from '@sofia/shared-types';

export const TemplateStudioView: React.FC = () => {
  const { t } = useI18n();
  const domains = getAdminDomains();

  // State: Pre-populated with default platform templates so UI is never blank
  const [activeTab, setActiveTab] = useState<PlatformAppTarget>('webLanding');
  const [settings, setSettings] = useState<PlatformTemplatesSetting>(DEFAULT_PLATFORM_TEMPLATES);
  const [loading, setLoading] = useState<boolean>(false);

  // Modals state
  const [activatingTemplate, setActivatingTemplate] = useState<{
    appTarget: PlatformAppTarget;
    template: TemplateMetadata;
  } | null>(null);
  const [isActivating, setIsActivating] = useState<boolean>(false);

  const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [registerForm, setRegisterForm] = useState({
    id: '',
    name: '',
    description: '',
    author: 'Four Elements Corp',
    version: '1.0.0',
    tags: 'Modern, Tailwind 3.x, Responsive',
    thumbnailUrl: '',
    previewPath: '',
  });

  // Toast state
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  }, []);

  // Fetch settings from API
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/templates`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (res.ok) {
        const envelope = (await res.json()) as { success: boolean; data: PlatformTemplatesSetting };
        if (envelope.success && envelope.data) {
          setSettings(envelope.data);
        }
      } else if (res.status === 401 || res.status === 403) {
        // In local development or before admin login, gracefully fallback to public active templates
        const pubRes = await fetch(`${domains.apiOrigin}/api/v1/public/templates`);
        if (pubRes.ok) {
          const pubData = (await pubRes.json()) as any;
          if (pubData?.data) {
            setSettings((prev) => ({
              ...prev,
              webLanding: {
                ...prev.webLanding,
                activeTemplate: pubData.data.webLanding || prev.webLanding.activeTemplate,
              },
              userWorkspace: {
                ...prev.userWorkspace,
                activeTemplate: pubData.data.userWorkspace || prev.userWorkspace.activeTemplate,
              },
              adminPortal: {
                ...prev.adminPortal,
                activeTemplate: pubData.data.adminPortal || prev.adminPortal.activeTemplate,
              },
            }));
          }
        }
      }
    } catch (err: any) {
      console.warn('[TemplateStudio] Failed to load server settings, retaining default catalog:', err);
    } finally {
      setLoading(false);
    }
  }, [domains.apiOrigin]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Activate Template Action
  const handleConfirmActivate = async () => {
    if (!activatingTemplate) return;

    try {
      setIsActivating(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/templates/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          app: activatingTemplate.appTarget,
          templateId: activatingTemplate.template.id,
        }),
      });

      if (!res.ok) {
        const errorData = (await res.json().catch(() => ({}))) as any;
        throw new Error(errorData?.error?.message || `HTTP ${res.status}`);
      }

      const envelope = (await res.json()) as { success: boolean; data: PlatformTemplatesSetting };
      if (envelope.success && envelope.data) {
        setSettings(envelope.data);
      } else {
        // Fallback update local state
        setSettings((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            [activatingTemplate.appTarget]: {
              ...prev[activatingTemplate.appTarget],
              activeTemplate: activatingTemplate.template.id,
            },
          };
        });
      }

      showToast(
        'success',
        t('admin.templateStudio.toast.activateSuccess', {
          templateName: activatingTemplate.template.name,
          appTarget: t(`admin.templateStudio.tabs.${activatingTemplate.appTarget}` as any),
        })
      );
      setActivatingTemplate(null);
    } catch (err: any) {
      showToast('error', t('admin.templateStudio.toast.activateError', { error: err?.message }));
    } finally {
      setIsActivating(false);
    }
  };

  // Emergency Reset Action
  const handleConfirmReset = async () => {
    try {
      setIsResetting(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/templates/reset`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!res.ok) {
        const errorData = (await res.json().catch(() => ({}))) as any;
        throw new Error(errorData?.error?.message || `HTTP ${res.status}`);
      }

      const envelope = (await res.json()) as { success: boolean; data: PlatformTemplatesSetting };
      if (envelope.success && envelope.data) {
        setSettings(envelope.data);
      }

      showToast('success', t('admin.templateStudio.toast.resetSuccess'));
      setIsResetModalOpen(false);
    } catch (err: any) {
      showToast('error', t('admin.templateStudio.toast.resetError', { error: err?.message }));
    } finally {
      setIsResetting(false);
    }
  };

  // Register Custom Template Action
  const handleConfirmRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!registerForm.id.trim() || !registerForm.name.trim()) {
      showToast('error', 'Please fill in required fields (Template ID and Name).');
      return;
    }

    try {
      setIsRegistering(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/templates/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          app: activeTab,
          template: {
            id: registerForm.id.trim().toLowerCase(),
            name: registerForm.name.trim(),
            description: registerForm.description.trim(),
            author: registerForm.author.trim() || 'Custom Developer',
            version: registerForm.version.trim() || '1.0.0',
            tags: registerForm.tags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
            thumbnailUrl: registerForm.thumbnailUrl.trim() || `/assets/templates/${registerForm.id}.png`,
            isBuiltIn: false,
            previewPath: registerForm.previewPath.trim() || `/?template=${registerForm.id.trim().toLowerCase()}`,
          },
        }),
      });

      if (!res.ok) {
        const errorData = (await res.json().catch(() => ({}))) as any;
        throw new Error(errorData?.error?.message || `HTTP ${res.status}`);
      }

      const envelope = (await res.json()) as { success: boolean; data: PlatformTemplatesSetting };
      if (envelope.success && envelope.data) {
        setSettings(envelope.data);
      }

      showToast(
        'success',
        t('admin.templateStudio.toast.registerSuccess', { templateName: registerForm.name })
      );
      setIsRegisterModalOpen(false);
      setRegisterForm({
        id: '',
        name: '',
        description: '',
        author: 'Four Elements Corp',
        version: '1.0.0',
        tags: 'Modern, Tailwind 3.x, Responsive',
        thumbnailUrl: '',
        previewPath: '',
      });
    } catch (err: any) {
      showToast('error', t('admin.templateStudio.toast.registerError', { error: err?.message }));
    } finally {
      setIsRegistering(false);
    }
  };

  // Live Preview Link Resolver
  const getPreviewUrl = (appTarget: PlatformAppTarget, template: TemplateMetadata): string => {
    const path = template.previewPath || `/?preview_template=${encodeURIComponent(template.id)}`;
    switch (appTarget) {
      case 'webLanding':
        return `${domains.siteOrigin}${path.startsWith('/') ? path : `/${path}`}`;
      case 'userWorkspace':
        return `${domains.appOrigin}${path.startsWith('/') ? path : `/${path}`}`;
      case 'adminPortal':
        return `${domains.adminOrigin}${path.startsWith('/') ? path : `/${path}`}`;
      default:
        return `${domains.siteOrigin}${path.startsWith('/') ? path : `/${path}`}`;
    }
  };

  const openLivePreview = (template: TemplateMetadata) => {
    const url = getPreviewUrl(activeTab, template);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const currentTabConfig = settings ? settings[activeTab] : null;
  const activeTemplateId = currentTabConfig?.activeTemplate || 'default';
  const availableTemplates = currentTabConfig?.availableTemplates || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl border transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-emerald-950/90 border-emerald-500/30 text-emerald-200'
              : 'bg-rose-950/90 border-rose-500/30 text-rose-200'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0" />
          )}
          <span className="text-xs font-medium max-w-sm">{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="p-1 rounded-lg hover:bg-white/10 transition-colors ml-2"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-surface-border">
        <div className="flex items-start gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-primary/20 via-accent/20 to-primary/5 border border-primary/20 flex items-center justify-center text-primary shadow-glow-primary/10 shrink-0">
            <LayoutTemplate className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-heading font-bold text-foreground">
                {t('admin.templateStudio.title')}
              </h1>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-primary/10 text-primary border border-primary/20">
                Multi-App Engine
              </span>
            </div>
            <p className="text-xs text-muted-fg mt-1 max-w-2xl">
              {t('admin.templateStudio.subtitle')}
            </p>
          </div>
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2.5 shrink-0">
          <button
            type="button"
            onClick={fetchSettings}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-muted-fg hover:text-foreground bg-surface-elevated hover:bg-surface border border-surface-border transition-colors disabled:opacity-50"
            title={t('admin.templateStudio.actions.refresh')}
          >
            <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-primary' : ''}`} />
            <span>{t('admin.templateStudio.actions.refresh')}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsResetModalOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium text-rose-400 hover:text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 transition-colors"
            title={t('admin.templateStudio.actions.resetAll')}
          >
            <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
            <span>{t('admin.templateStudio.actions.resetAll')}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsRegisterModalOpen(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold text-white bg-primary hover:bg-primary-hover shadow-sm transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t('admin.templateStudio.actions.registerNew')}</span>
          </button>
        </div>
      </div>

      {/* Quick Status KPI Strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        {/* Web Landing Status */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
              <Globe className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-muted-fg">
                {t('admin.templateStudio.quickStatus.webLanding')}
              </div>
              <div className="text-sm font-heading font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                <span>{settings?.webLanding?.activeTemplate || 'default'}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-lg text-[10px] font-mono font-medium bg-surface-elevated text-muted-fg border border-surface-border">
            {settings?.webLanding?.availableTemplates?.length || 0} templates
          </span>
        </div>

        {/* User Workspace Status */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
              <Laptop className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-muted-fg">
                {t('admin.templateStudio.quickStatus.userWorkspace')}
              </div>
              <div className="text-sm font-heading font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                <span>{settings?.userWorkspace?.activeTemplate || 'default'}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-lg text-[10px] font-mono font-medium bg-surface-elevated text-muted-fg border border-surface-border">
            {settings?.userWorkspace?.availableTemplates?.length || 0} templates
          </span>
        </div>

        {/* Admin Portal Status */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold tracking-wider text-muted-fg">
                {t('admin.templateStudio.quickStatus.adminPortal')}
              </div>
              <div className="text-sm font-heading font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                <span>{settings?.adminPortal?.activeTemplate || 'default'}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            </div>
          </div>
          <span className="px-2 py-0.5 rounded-lg text-[10px] font-mono font-medium bg-surface-elevated text-muted-fg border border-surface-border">
            {settings?.adminPortal?.availableTemplates?.length || 0} templates
          </span>
        </div>
      </div>

      {/* App Target Navigation Tabs */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 p-1 bg-surface-elevated rounded-2xl border border-surface-border max-w-fit">
          <button
            type="button"
            onClick={() => setActiveTab('webLanding')}
            className={`flex items-center gap-2.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'webLanding'
                ? 'bg-surface text-foreground shadow-sm border border-surface-border'
                : 'text-muted-fg hover:text-foreground'
            }`}
          >
            <Globe className="w-4 h-4 text-blue-400" />
            <span>{t('admin.templateStudio.tabs.webLanding')}</span>
            <span className="px-1.5 py-0.2 rounded-md text-[10px] font-mono bg-blue-500/10 text-blue-400">
              {settings?.webLanding?.availableTemplates?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('userWorkspace')}
            className={`flex items-center gap-2.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'userWorkspace'
                ? 'bg-surface text-foreground shadow-sm border border-surface-border'
                : 'text-muted-fg hover:text-foreground'
            }`}
          >
            <Laptop className="w-4 h-4 text-purple-400" />
            <span>{t('admin.templateStudio.tabs.userWorkspace')}</span>
            <span className="px-1.5 py-0.2 rounded-md text-[10px] font-mono bg-purple-500/10 text-purple-400">
              {settings?.userWorkspace?.availableTemplates?.length || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('adminPortal')}
            className={`flex items-center gap-2.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === 'adminPortal'
                ? 'bg-surface text-foreground shadow-sm border border-surface-border'
                : 'text-muted-fg hover:text-foreground'
            }`}
          >
            <Shield className="w-4 h-4 text-amber-400" />
            <span>{t('admin.templateStudio.tabs.adminPortal')}</span>
            <span className="px-1.5 py-0.2 rounded-md text-[10px] font-mono bg-amber-500/10 text-amber-400">
              {settings?.adminPortal?.availableTemplates?.length || 0}
            </span>
          </button>
        </div>

        {/* Tab Context Description */}
        <p className="text-xs text-muted-fg flex items-center gap-1.5 px-1">
          <Info className="w-3.5 h-3.5 text-primary shrink-0" />
          <span>{t(`admin.templateStudio.tabs.${activeTab}Desc` as any)}</span>
        </p>
      </div>

      {/* Loading Skeleton */}
      {loading && !settings ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="h-80 rounded-2xl bg-surface animate-pulse border border-surface-border p-5 flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="h-32 bg-surface-elevated rounded-xl" />
                <div className="h-4 w-3/4 bg-surface-elevated rounded" />
                <div className="h-3 w-1/2 bg-surface-elevated rounded" />
              </div>
              <div className="h-9 bg-surface-elevated rounded-xl" />
            </div>
          ))}
        </div>
      ) : (
        /* Template Catalog Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {availableTemplates.map((template) => {
            const isActive = template.id === activeTemplateId;

            return (
              <div
                key={template.id}
                className={`group relative rounded-2xl bg-surface border transition-all duration-300 flex flex-col justify-between overflow-hidden ${
                  isActive
                    ? 'border-primary shadow-glow-primary/20 ring-1 ring-primary'
                    : 'border-surface-border hover:border-primary/40 hover:shadow-lg'
                }`}
              >
                {/* Visual Layout Mockup Header */}
                <div className="h-36 bg-gradient-to-br from-surface-elevated via-surface to-surface-elevated p-4 border-b border-surface-border relative flex flex-col justify-between overflow-hidden">
                  {/* Decorative background grid pattern */}
                  <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:12px_12px]" />

                  {/* Top Badges */}
                  <div className="relative z-10 flex items-center justify-between gap-2">
                    {/* Active Status Badge */}
                    {isActive ? (
                      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shadow-sm">
                        <CheckCheck className="w-3 h-3 text-emerald-400" />
                        {t('admin.templateStudio.activeBadge')}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface/80 backdrop-blur text-muted-fg border border-surface-border">
                        {template.isBuiltIn
                          ? t('admin.templateStudio.builtInBadge')
                          : t('admin.templateStudio.customBadge')}
                      </span>
                    )}

                    {/* Version Badge */}
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono text-muted-fg bg-surface/80 border border-surface-border">
                      v{template.version}
                    </span>
                  </div>

                  {/* Interactive Layout Wireframe Thumbnail */}
                  <div className="relative z-10 flex items-center justify-center py-2">
                    <LayoutMockupWireframe appTarget={activeTab} templateId={template.id} />
                  </div>

                  {/* Bottom ID Chip */}
                  <div className="relative z-10 flex items-center justify-between text-[10px] text-muted-fg font-mono">
                    <span>ID: {template.id}</span>
                    <span>{template.author}</span>
                  </div>
                </div>

                {/* Card Content Body */}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div>
                    <h3 className="text-base font-heading font-bold text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                      <span>{template.name}</span>
                      {isActive && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      )}
                    </h3>

                    <p className="text-xs text-muted-fg line-clamp-3 mt-1.5 leading-relaxed">
                      {template.description}
                    </p>
                  </div>

                  {/* Tags Pill List */}
                  {template.tags && template.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {template.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded-lg text-[10px] font-medium bg-surface-elevated text-foreground/80 border border-surface-border/60"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Card Action Buttons */}
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-surface-border/60">
                    {/* Live Preview Button */}
                    <button
                      type="button"
                      onClick={() => openLivePreview(template)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground bg-surface-elevated hover:bg-surface border border-surface-border transition-colors"
                      title={t('admin.templateStudio.actions.preview')}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>{t('admin.templateStudio.actions.preview')}</span>
                    </button>

                    {/* Activate Button */}
                    {isActive ? (
                      <button
                        type="button"
                        disabled
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 cursor-default"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>{t('admin.templateStudio.actions.active')}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setActivatingTemplate({
                            appTarget: activeTab,
                            template,
                          })
                        }
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-primary hover:bg-primary-hover shadow-sm transition-all"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{t('admin.templateStudio.actions.activate')}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal: Activate Template */}
      {activatingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-5 animate-scale-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-heading font-bold text-foreground">
                  {t('admin.templateStudio.activateModal.title')}
                </h3>
                <p className="text-xs text-muted-fg leading-relaxed">
                  {t('admin.templateStudio.activateModal.description', {
                    appTarget: t(`admin.templateStudio.tabs.${activatingTemplate.appTarget}` as any),
                    templateName: activatingTemplate.template.name,
                  })}
                </p>
              </div>
            </div>

            {/* Warning Box */}
            <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-2.5 text-amber-300 text-xs leading-relaxed">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <span>{t('admin.templateStudio.activateModal.warning')}</span>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setActivatingTemplate(null)}
                disabled={isActivating}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground bg-surface-elevated hover:bg-surface border border-surface-border transition-colors disabled:opacity-50"
              >
                {t('admin.templateStudio.actions.cancel')}
              </button>

              <button
                type="button"
                onClick={handleConfirmActivate}
                disabled={isActivating}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-primary hover:bg-primary-hover shadow-sm transition-colors disabled:opacity-50"
              >
                {isActivating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>
                  {isActivating
                    ? t('admin.templateStudio.actions.activating')
                    : t('admin.templateStudio.activateModal.confirmButton')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal: Emergency Reset to Defaults */}
      {isResetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-5 animate-scale-in">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base font-heading font-bold text-foreground">
                  {t('admin.templateStudio.resetModal.title')}
                </h3>
                <p className="text-xs text-muted-fg leading-relaxed">
                  {t('admin.templateStudio.resetModal.description')}
                </p>
              </div>
            </div>

            {/* Warning Box */}
            <div className="p-3.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs leading-relaxed">
              {t('admin.templateStudio.resetModal.warning')}
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setIsResetModalOpen(false)}
                disabled={isResetting}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground bg-surface-elevated hover:bg-surface border border-surface-border transition-colors disabled:opacity-50"
              >
                {t('admin.templateStudio.actions.cancel')}
              </button>

              <button
                type="button"
                onClick={handleConfirmReset}
                disabled={isResetting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 shadow-sm transition-colors disabled:opacity-50"
              >
                {isResetting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>{t('admin.templateStudio.resetModal.confirmButton')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Register Custom Template */}
      {isRegisterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-lg rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-5 animate-scale-in">
            <div className="flex items-center justify-between pb-3 border-b border-surface-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <Plus className="w-4 h-4" />
                </div>
                <h3 className="text-base font-heading font-bold text-foreground">
                  {t('admin.templateStudio.registerModal.title')}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsRegisterModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleConfirmRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {/* Template ID (Slug) */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-fg">
                    {t('admin.templateStudio.registerModal.idLabel')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={registerForm.id}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({
                        ...prev,
                        id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                      }))
                    }
                    placeholder={t('admin.templateStudio.registerModal.idPlaceholder')}
                    className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border focus:border-primary focus:outline-none font-mono"
                  />
                </div>

                {/* Display Name */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-fg">
                    {t('admin.templateStudio.registerModal.nameLabel')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={registerForm.name}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder={t('admin.templateStudio.registerModal.namePlaceholder')}
                    className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-fg">
                  {t('admin.templateStudio.registerModal.descLabel')}
                </label>
                <textarea
                  rows={2}
                  value={registerForm.description}
                  onChange={(e) =>
                    setRegisterForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder={t('admin.templateStudio.registerModal.descPlaceholder')}
                  className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border focus:border-primary focus:outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Author */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-fg">
                    {t('admin.templateStudio.registerModal.authorLabel')}
                  </label>
                  <input
                    type="text"
                    value={registerForm.author}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({ ...prev, author: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border focus:border-primary focus:outline-none"
                  />
                </div>

                {/* Version */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-muted-fg">
                    {t('admin.templateStudio.registerModal.versionLabel')}
                  </label>
                  <input
                    type="text"
                    value={registerForm.version}
                    onChange={(e) =>
                      setRegisterForm((prev) => ({ ...prev, version: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border focus:border-primary focus:outline-none font-mono"
                  />
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-fg">
                  {t('admin.templateStudio.registerModal.tagsLabel')}
                </label>
                <input
                  type="text"
                  value={registerForm.tags}
                  onChange={(e) => setRegisterForm((prev) => ({ ...prev, tags: e.target.value }))}
                  placeholder={t('admin.templateStudio.registerModal.tagsPlaceholder')}
                  className="w-full px-3 py-2 rounded-xl text-xs bg-surface-elevated border border-surface-border focus:border-primary focus:outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-surface-border">
                <button
                  type="button"
                  onClick={() => setIsRegisterModalOpen(false)}
                  disabled={isRegistering}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground bg-surface-elevated hover:bg-surface border border-surface-border transition-colors disabled:opacity-50"
                >
                  {t('admin.templateStudio.actions.cancel')}
                </button>

                <button
                  type="submit"
                  disabled={isRegistering}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-primary hover:bg-primary-hover shadow-sm transition-colors disabled:opacity-50"
                >
                  {isRegistering && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <span>{t('admin.templateStudio.registerModal.submitButton')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * LayoutMockupWireframe Component
 * Renders stylized layout archetype diagrams matching template designs.
 */
interface LayoutMockupWireframeProps {
  appTarget: PlatformAppTarget;
  templateId: string;
}

const LayoutMockupWireframe: React.FC<LayoutMockupWireframeProps> = ({
  appTarget,
  templateId,
}) => {
  // Render wireframes for User Workspace
  if (appTarget === 'userWorkspace') {
    if (templateId === 'executive') {
      // Top Navbar Grid
      return (
        <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex flex-col gap-1 shadow-inner">
          <div className="h-2 w-full bg-primary/30 rounded flex items-center px-1">
            <div className="w-2 h-1 bg-primary rounded-sm" />
          </div>
          <div className="flex-1 grid grid-cols-3 gap-1">
            <div className="col-span-2 bg-surface-elevated rounded p-0.5 space-y-0.5">
              <div className="h-1.5 w-3/4 bg-primary/20 rounded" />
              <div className="h-1 w-full bg-muted-fg/20 rounded" />
            </div>
            <div className="bg-surface-elevated rounded" />
          </div>
        </div>
      );
    }

    if (templateId === 'minimal-zen') {
      // Zen Distraction Free Center
      return (
        <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-2 flex items-center justify-center shadow-inner">
          <div className="w-20 h-10 bg-surface-elevated rounded border border-surface-border p-1 space-y-1">
            <div className="h-1.5 w-1/2 bg-primary/30 rounded mx-auto" />
            <div className="h-1 w-3/4 bg-muted-fg/20 rounded mx-auto" />
          </div>
        </div>
      );
    }

    if (templateId === 'studio-immersive') {
      // Dark Canvas 3-pane
      return (
        <div className="w-32 h-16 rounded-lg bg-black/60 border border-primary/30 p-1 flex gap-1 shadow-inner">
          <div className="w-4 h-full bg-primary/20 rounded" />
          <div className="flex-1 bg-surface-elevated rounded p-1 flex flex-col justify-center items-center">
            <div className="w-6 h-6 rounded-full border border-primary/40 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-primary" />
            </div>
          </div>
          <div className="w-6 h-full bg-surface-elevated rounded" />
        </div>
      );
    }

    // Default User Workspace: Dual Sidebar Collapsible
    return (
      <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex gap-1 shadow-inner">
        <div className="w-3 h-full bg-primary/30 rounded" />
        <div className="w-6 h-full bg-surface-elevated rounded space-y-0.5 p-0.5">
          <div className="h-1 w-full bg-muted-fg/30 rounded" />
          <div className="h-1 w-3/4 bg-muted-fg/20 rounded" />
        </div>
        <div className="flex-1 bg-surface-elevated rounded p-1 space-y-1">
          <div className="h-1.5 w-1/2 bg-primary/20 rounded" />
          <div className="h-1 w-full bg-muted-fg/15 rounded" />
        </div>
      </div>
    );
  }

  // Render wireframes for Admin Portal
  if (appTarget === 'adminPortal') {
    if (templateId === 'cyberpunk-noc') {
      // High-Density Matrix
      return (
        <div className="w-32 h-16 rounded-lg bg-emerald-950/40 border border-emerald-500/40 p-1 grid grid-cols-4 gap-0.5 shadow-inner">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-emerald-500/20 rounded-sm" />
          ))}
        </div>
      );
    }

    if (templateId === 'executive-bi') {
      // Analytics First Chart Shell
      return (
        <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex flex-col gap-1 shadow-inner">
          <div className="h-2 w-full bg-surface-elevated rounded flex items-center px-1">
            <div className="w-3 h-1 bg-amber-400/40 rounded" />
          </div>
          <div className="flex-1 grid grid-cols-2 gap-1">
            <div className="bg-surface-elevated rounded flex items-end p-0.5 gap-0.5">
              <div className="h-2 w-1 bg-primary/40 rounded-t" />
              <div className="h-4 w-1 bg-primary/60 rounded-t" />
              <div className="h-6 w-1 bg-primary rounded-t" />
            </div>
            <div className="bg-surface-elevated rounded" />
          </div>
        </div>
      );
    }

    // Default Admin Portal
    return (
      <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex gap-1 shadow-inner">
        <div className="w-5 h-full bg-primary/20 rounded" />
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-2 w-full bg-surface-elevated rounded" />
          <div className="flex-1 bg-surface-elevated rounded" />
        </div>
      </div>
    );
  }

  // Render wireframes for Web Landing
  if (templateId === 'fe-genai-classic' || templateId === 'fe-genai-dark' || templateId === 'fe-genai-light') {
    // Four Elements AI Home 1: Typewriter Hero + 6 Features + Dual Marquee
    return (
      <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex flex-col gap-1 shadow-inner">
        <div className="h-2 w-full bg-surface-elevated rounded flex items-center justify-between px-1">
          <div className="w-2 h-1 bg-primary rounded-sm" />
          <div className="w-8 h-1 bg-muted-fg/30 rounded-sm" />
        </div>
        <div className="h-4 w-full bg-primary/20 rounded flex flex-col items-center justify-center px-1">
          <div className="w-16 h-1 bg-primary rounded" />
          <div className="w-10 h-0.5 bg-accent rounded mt-0.5" />
        </div>
        <div className="flex-1 grid grid-cols-3 gap-0.5">
          <div className="bg-surface-elevated rounded" />
          <div className="bg-surface-elevated rounded" />
          <div className="bg-surface-elevated rounded" />
        </div>
      </div>
    );
  }

  if (templateId === 'fe-genai-modern' || templateId === 'fe-genai-modern-dark' || templateId === 'fe-genai-modern-light') {
    // Four Elements AI Home 2: Asymmetric Hero + Interactive Tabs
    return (
      <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex flex-col gap-1 shadow-inner">
        <div className="h-2 w-full bg-surface-elevated rounded flex items-center justify-between px-1">
          <div className="w-2 h-1 bg-accent rounded-sm" />
          <div className="w-8 h-1 bg-muted-fg/30 rounded-sm" />
        </div>
        <div className="flex-1 grid grid-cols-12 gap-1 items-center">
          <div className="col-span-5 space-y-1">
            <div className="w-full h-1 bg-primary/40 rounded" />
            <div className="w-3/4 h-1 bg-muted-fg/20 rounded" />
          </div>
          <div className="col-span-7 h-full bg-surface-elevated rounded border border-surface-border/50" />
        </div>
      </div>
    );
  }

  if (templateId === 'minimal') {
    // Single Column Minimalist
    return (
      <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-2 flex flex-col items-center justify-center gap-1 shadow-inner">
        <div className="h-2 w-16 bg-foreground/30 rounded" />
        <div className="h-1 w-20 bg-muted-fg/20 rounded" />
        <div className="h-3 w-8 bg-primary/40 rounded mt-1" />
      </div>
    );
  }

  if (templateId === 'enterprise') {
    // Enterprise B2B Bento
    return (
      <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex flex-col gap-1 shadow-inner">
        <div className="h-2 w-full bg-surface-elevated rounded" />
        <div className="flex-1 grid grid-cols-3 gap-0.5">
          <div className="col-span-2 bg-blue-500/20 rounded" />
          <div className="bg-surface-elevated rounded" />
        </div>
      </div>
    );
  }

  // Default Web Landing: Sophia Astro Hero + Bento
  return (
    <div className="w-32 h-16 rounded-lg bg-surface border border-surface-border p-1 flex flex-col gap-1 shadow-inner">
      <div className="h-2 w-full bg-surface-elevated rounded flex items-center justify-between px-1">
        <div className="w-2 h-1 bg-primary rounded-sm" />
        <div className="w-6 h-1 bg-muted-fg/30 rounded-sm" />
      </div>
      <div className="h-5 w-full bg-primary/10 rounded flex items-center justify-center">
        <div className="w-12 h-1.5 bg-primary/40 rounded" />
      </div>
      <div className="flex-1 grid grid-cols-3 gap-0.5">
        <div className="bg-surface-elevated rounded" />
        <div className="bg-surface-elevated rounded" />
        <div className="bg-surface-elevated rounded" />
      </div>
    </div>
  );
};
